import { AccountTypeEnum, ComplianceObjectType } from "~/graphql/supergraphTypes";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { isIndividualProfile, isJointProfile } from "~/pages/Compliance/utils/accountTypeUtils";
import { formatShortFormDate } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";
import { programsFromPositions } from "../CompareTable/utils";
import type { ProfileDetail, ProfileListItem, ProfileTag } from "./ProfileSelection/types";
import type { RankedProfilesQuery } from "~/graphql/supergraphTypes";

// The ":" keeps these collision-proof against real profile IDs (base64 relay IDs never contain one).
export const CREATE_NEW_OPTION_ID = "place_holder:new_profile";
export const NONE_OF_THE_ABOVE_OPTION_ID = "place_holder:none_of_the_above";

export const MIN_SEARCH_LENGTH = 3;

// Digits with optional spaces/hyphens only between them (e.g. "123-45-6789").
export const TAX_ID_PATTERN = /^\d+(?:[-\s]+\d+)*$/;

export type RankedProfileItem = RankedProfilesQuery["rankedProfile"]["profiles"][number];
export type SearchMatch = "taxId" | "name" | null;
export type ProfileExtraOption = "create" | "none-of-the-above" | null;
export type ProfileListMode = "pending" | "investment-account" | "search";

// Digits (with optional spaces/hyphens) -> tax ID, else name. Sent as-is; short values skip.
export const deriveSearchArgs = (
  rawSearch: string,
): { matchedBy: SearchMatch; taxId?: string; name?: string } => {
  const trimmed = rawSearch.trim();
  if (trimmed.length < MIN_SEARCH_LENGTH) return { matchedBy: null };
  if (TAX_ID_PATTERN.test(trimmed)) return { matchedBy: "taxId", taxId: trimmed };
  return { matchedBy: "name", name: trimmed };
};

// Derives which list ProfileIntro should render, and whether Back should return to the
// investment-account list instead of leaving the step (only once search was reached from a
// populated IA list via "None of the above").
export const deriveProfileMode = ({
  resolveSettled,
  investmentAccountResolved,
  forceSearch,
  iaQuerySettled,
  hasIaProfiles,
}: {
  resolveSettled: boolean;
  investmentAccountResolved: boolean;
  forceSearch: boolean;
  iaQuerySettled: boolean;
  hasIaProfiles: boolean;
}): {
  mode: ProfileListMode;
  isSearchHidden: boolean;
  extraOption: ProfileExtraOption;
  canReturnToAccountList: boolean;
} => {
  // Resolution (or the initial IA lookup) is still in flight; keep search hidden so it
  // doesn't flash before the mode is known.
  if (!resolveSettled || (investmentAccountResolved && !iaQuerySettled)) {
    return {
      mode: "pending",
      isSearchHidden: true,
      extraOption: null,
      canReturnToAccountList: false,
    };
  }

  // The IA drives the list until the user opts into free search via "None of the above".
  if (investmentAccountResolved && hasIaProfiles && !forceSearch) {
    return {
      mode: "investment-account",
      isSearchHidden: true,
      extraOption: "none-of-the-above",
      canReturnToAccountList: false,
    };
  }

  // Creating a profile is offered only when the IA has no associated profiles (or no IA at all).
  // When the IA does have associates, search is for finding another existing profile — no create.
  const hasAssociatedProfiles = investmentAccountResolved && hasIaProfiles;
  return {
    mode: "search",
    isSearchHidden: false,
    extraOption: hasAssociatedProfiles ? null : "create",
    canReturnToAccountList: hasAssociatedProfiles,
  };
};

const formatPositionName = (position: RankedProfileItem["positions"][number]): string =>
  position.account?.name
    ? `${position.account.name} in ${position.investmentEntity.name}`
    : position.investmentEntity.name;

const buildDetails = (profile: RankedProfileItem): ProfileDetail[] => {
  const details: ProfileDetail[] = [];
  const accounts = profile.associatedInvestmentAccounts;
  if (accounts.length > 0) {
    const additionalCount = accounts.length - 1;
    details.push({
      label: "Investment account",
      value: accounts[0].name,
      additionalCount,
      tooltip:
        additionalCount > 0 ? accounts.map((account) => account.name).join("\n") : undefined,
    });
  }
  if (profile.positions.length > 0) {
    const [first, ...rest] = profile.positions;
    details.push({
      label: "Position",
      value: first.investmentEntity.name,
      additionalCount: rest.length,
      tooltip: rest.length > 0 ? rest.map(formatPositionName).join("\n") : undefined,
    });
  }
  return details;
};

const buildTags = (profile: RankedProfileItem, matchedBy: SearchMatch): ProfileTag[] => {
  const tags: ProfileTag[] = [];
  if (matchedBy === "taxId") tags.push({ label: "Tax ID matches" });
  if (profile.lastUsedOnInvestmentAccount) tags.push({ label: "Recently used" });
  if (profile.matchPercent != null) {
    const percent = Math.round(profile.matchPercent);
    tags.push({ label: `${percent}% Match`, variant: percent === 100 ? "success" : undefined });
  }
  return tags;
};

const getAccountTypeLabel = (
  accountType: AccountTypeEnum | null | undefined,
  complianceObjectType: ComplianceObjectType | null | undefined,
): string => {
  if (isIndividualProfile(accountType, complianceObjectType)) {
    return "Individual";
  }
  if (isJointProfile(accountType, complianceObjectType)) {
    return "Joint ownership account";
  }
  return "Entity";
};

export const rankedProfileToListItem = (
  profile: RankedProfileItem,
  matchedBy: SearchMatch = null,
): ProfileListItem => ({
  id: String(profile.id),
  guid: profile.profileGuid ?? null,
  name: getInvestingProfileName(profile),
  details: buildDetails(profile),
  lastUsed: formatShortFormDate(profile.lastUsedOnIO),
  accountType: getAccountTypeLabel(profile.accountType, profile.complianceObjectType),
  tags: buildTags(profile, matchedBy),
  programs: programsFromPositions(profile.positions),
  associatedInvestmentAccountIds: profile.associatedInvestmentAccounts.map((account) =>
    toDbId(account.id),
  ),
});
